// src/components/useVoiceInput.js
//
// Talk to the coach. Tap the microphone, say it, tap again: the recording is
// turned into text on the server and lands in the composer to be read and
// sent — never sent on its own, because a misheard number is a wrong
// request.
//
// Recording is MediaRecorder; before upload the clip is converted to 16 kHz
// mono WAV in the browser, so the server always gets the one format the
// speech model certainly reads (Safari records AAC in MP4, Chrome Opus in
// WebM). If the conversion is not possible the original clip is sent.
//
// States: idle → requesting (permission) → recording → transcribing → idle.

import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE } from "../api";

export const VOICE_MAX_SECONDS = 60;
const BARS = 5;
/* Below this the room was silent: there is nothing to transcribe, and a
   speech model asked to transcribe silence invents a sentence. */
const SILENCE_PEAK = 0.02;

export function voiceSupported() {
  return typeof window !== "undefined"
    && typeof navigator !== "undefined"
    && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
    && typeof window.MediaRecorder === "function";
}

function pickMime() {
  const MR = window.MediaRecorder;
  for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac"]) {
    try { if (MR.isTypeSupported && MR.isTypeSupported(m)) return m; } catch (_) { /* keep looking */ }
  }
  return "";
}

/** 16-bit PCM WAV from one channel of float samples. */
export function encodeWav(samples, sampleRate) {
  const buf = new ArrayBuffer(44 + samples.length * 2);
  const v = new DataView(buf);
  const str = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  str(0, "RIFF"); v.setUint32(4, 36 + samples.length * 2, true); str(8, "WAVE");
  str(12, "fmt "); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, sampleRate, true); v.setUint32(28, sampleRate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  str(36, "data"); v.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    v.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buf;
}

/** The recorded clip as 16 kHz mono WAV; null when the browser cannot. */
async function toWav16k(blob) {
  const AC = window.AudioContext || window.webkitAudioContext;
  const OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
  if (!AC || !OAC) return null;
  const ctx = new AC();
  try {
    const raw = await blob.arrayBuffer();
    // The callback form: older Safari has no promise form.
    const decoded = await new Promise((resolve, reject) => {
      const p = ctx.decodeAudioData(raw, resolve, reject);
      if (p && typeof p.then === "function") p.then(resolve, reject);
    });
    const RATE = 16000;
    const off = new OAC(1, Math.max(1, Math.ceil(decoded.duration * RATE)), RATE);
    const src = off.createBufferSource();
    src.buffer = decoded;
    src.connect(off.destination);
    src.start();
    const rendered = await off.startRendering();
    return new Blob([encodeWav(rendered.getChannelData(0), RATE)], { type: "audio/wav" });
  } catch (_) {
    return null;
  } finally {
    try { ctx.close(); } catch (_) { /* already closed */ }
  }
}

const micMessage = (err) => {
  const name = (err && err.name) || "";
  if (name === "NotAllowedError" || name === "SecurityError") return "Microphone access is off. Allow it for this app in your phone's settings, or use the keyboard's dictation.";
  if (name === "NotFoundError" || name === "OverconstrainedError") return "No microphone found.";
  if (name === "NotReadableError") return "The microphone is in use by another app.";
  return "Could not start recording.";
};

export function useVoiceInput({ onText }) {
  const [state, setState] = useState("idle"); // idle | requesting | recording | transcribing
  const [seconds, setSeconds] = useState(0);
  const [levels, setLevels] = useState(() => new Array(BARS).fill(0));
  const [error, setError] = useState("");

  const onTextRef = useRef(onText);
  onTextRef.current = onText;
  const rec = useRef(null);       // MediaRecorder
  const stream = useRef(null);
  const chunks = useRef([]);
  const audio = useRef(null);     // { ctx, analyser, data }
  const raf = useRef(0);
  const tick = useRef(0);
  const startedAt = useRef(0);
  const peak = useRef(0);
  const discard = useRef(false);
  const alive = useRef(true);

  const release = useCallback(() => {
    cancelAnimationFrame(raf.current);
    clearInterval(tick.current);
    if (stream.current) { try { stream.current.getTracks().forEach((t) => t.stop()); } catch (_) { /* gone */ } stream.current = null; }
    if (audio.current) { try { audio.current.ctx.close(); } catch (_) { /* gone */ } audio.current = null; }
    rec.current = null;
  }, []);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; discard.current = true; try { rec.current && rec.current.state !== "inactive" && rec.current.stop(); } catch (_) { /* gone */ } release(); };
  }, [release]);

  const finish = useCallback(async (mime) => {
    const blob = new Blob(chunks.current, { type: mime || "audio/webm" });
    chunks.current = [];
    const wasSilent = peak.current < SILENCE_PEAK;
    const tooShort = Date.now() - startedAt.current < 500;
    release();
    if (!alive.current) return;
    if (discard.current) { setState("idle"); return; }
    if (tooShort || blob.size < 1024) { setState("idle"); setError("That was too short. Hold on a little longer."); return; }
    if (wasSilent) { setState("idle"); setError("I didn't hear anything. Check the microphone and try again."); return; }
    setState("transcribing");
    try {
      const wav = await toWav16k(blob);
      const body = wav || blob;
      const res = await fetch(`${API_BASE}/api/coach/transcribe`, { method: "POST", credentials: "include", headers: { "Content-Type": body.type || "application/octet-stream" }, body });
      let json = null;
      try { json = await res.json(); } catch (_) { json = null; }
      if (!alive.current) return;
      if (!res.ok) throw new Error((json && json.error) || "Could not transcribe that. Try again, or type it.");
      const text = String((json && json.text) || "").trim();
      if (!text) setError("I didn't catch that. Try again, a little closer to the phone.");
      else if (onTextRef.current) onTextRef.current(text);
    } catch (err) {
      if (alive.current) setError((err && err.message) || "Could not transcribe that. Try again, or type it.");
    } finally {
      if (alive.current) setState("idle");
    }
  }, [release]);

  const stop = useCallback(() => {
    const r = rec.current;
    if (r && r.state !== "inactive") { try { r.stop(); } catch (_) { release(); setState("idle"); } }
  }, [release]);

  const cancel = useCallback(() => {
    discard.current = true;
    const r = rec.current;
    if (r && r.state !== "inactive") { try { r.stop(); } catch (_) { release(); setState("idle"); } }
    else { release(); setState("idle"); }
  }, [release]);

  const start = useCallback(async () => {
    if (!voiceSupported() || state !== "idle") return;
    setError("");
    setSeconds(0);
    setLevels(new Array(BARS).fill(0));
    discard.current = false;
    peak.current = 0;
    setState("requesting");
    let s;
    try {
      s = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 } });
    } catch (err) {
      if (alive.current) { setState("idle"); setError(micMessage(err)); }
      return;
    }
    if (!alive.current) { s.getTracks().forEach((t) => t.stop()); return; }
    stream.current = s;
    const mime = pickMime();
    let r;
    try {
      r = mime ? new window.MediaRecorder(s, { mimeType: mime }) : new window.MediaRecorder(s);
    } catch (err) {
      release();
      setState("idle");
      setError("Recording is not available in this browser.");
      return;
    }
    rec.current = r;
    chunks.current = [];
    r.ondataavailable = (e) => { if (e.data && e.data.size) chunks.current.push(e.data); };
    r.onstop = () => { finish(r.mimeType || mime); };
    r.onerror = () => { discard.current = true; release(); if (alive.current) { setState("idle"); setError("Recording stopped unexpectedly."); } };

    // The level meter: proof to the athlete that the phone is hearing them.
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      ctx.createMediaStreamSource(s).connect(analyser);
      const data = new Uint8Array(analyser.fftSize);
      audio.current = { ctx, analyser, data };
      let last = 0;
      const hist = new Array(BARS).fill(0);
      const loop = (now) => {
        raf.current = requestAnimationFrame(loop);
        if (!audio.current) return;
        analyser.getByteTimeDomainData(data);
        let max = 0;
        for (let i = 0; i < data.length; i++) { const v = Math.abs(data[i] - 128) / 128; if (v > max) max = v; }
        if (max > peak.current) peak.current = max;
        if (now - last < 90) return;
        last = now;
        hist.push(Math.min(1, max * 2.2));
        hist.shift();
        setLevels(hist.slice());
      };
      raf.current = requestAnimationFrame(loop);
    } catch (_) {
      // No meter: assume there was sound rather than refuse to transcribe.
      peak.current = 1;
    }

    startedAt.current = Date.now();
    try { r.start(250); } catch (_) { try { r.start(); } catch (err) { release(); setState("idle"); setError("Could not start recording."); return; } }
    setState("recording");
    tick.current = setInterval(() => {
      const sec = Math.floor((Date.now() - startedAt.current) / 1000);
      setSeconds(sec);
      if (sec >= VOICE_MAX_SECONDS) { clearInterval(tick.current); try { r.state !== "inactive" && r.stop(); } catch (_) { /* gone */ } }
    }, 250);
  }, [state, finish, release]);

  return { supported: voiceSupported(), state, seconds, levels, error, clearError: () => setError(""), start, stop, cancel };
}
